import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import type { DetailedChangeset } from 'ember-changeset/types';
import newGroupInviteChangeset from 'garaje/changesets/new-group-invite';
import type Flow from 'garaje/models/flow';
import GroupInvite from 'garaje/models/group-invite';
import type LocationModel from 'garaje/models/location';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import type TransitionConfirmService from 'garaje/services/transition-confirm';
import { routeEvent } from 'garaje/utils/decorators/route';
import handsontable from 'handsontable';

export interface VisitorsGroupInvitesShowRouteModel {
  changeset: DetailedChangeset<GroupInvite>;
  groupInvite: GroupInvite;
  parentRecurringGroupInvite?: RecurringGroupInvite;
  handsontable: unknown;
  location: LocationModel | null;
  flows: Flow[];
}

export default class VisitorsGroupInvitesShowRoute extends Route {
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare transitionConfirm: TransitionConfirmService;
  @service declare featureFlags: FeatureFlagsService;

  beforeModel(): void {
    if (
      !this.featureFlags.isEnabled('visitors-group-invite-updates-m1') ||
      !this.state.features?.canAccessGroupInvitesFeature
    ) {
      void this.router.replaceWith('visitors.invites.index');
    }
  }

  async model({
    group_invite_id: groupInviteId,
  }: {
    group_invite_id: string;
  }): Promise<VisitorsGroupInvitesShowRouteModel> {
    const { currentLocation: location } = this.state;
    const include = GroupInvite.groupInviteIncludes({
      includeRecurringGroupInvites: true,
      includeGroupInviteLinks: this.featureFlags.isEnabled('visitors-generic-invite-link'),
    });

    const reload = GroupInvite.shouldReloadRecord(this.store, groupInviteId);
    const groupInvite = await this.store.findRecord('group-invite', groupInviteId, { include, reload });
    const parentRecurringGroupInvite = isPresent(groupInvite.belongsTo('recurringGroupInvite').id())
      ? <RecurringGroupInvite>groupInvite.belongsTo('recurringGroupInvite').value()
      : undefined;
    if (!groupInvite) void this.router.transitionTo('/not-found');

    const changeset = newGroupInviteChangeset(groupInvite);

    const groupInviteLocation = groupInvite.location;

    if (groupInviteLocation?.id) {
      this.maybeSwitchCurrentLocation(location?.id, groupInviteLocation.id);
    }

    const flows = await this.state.loadFlows({ reload: false, locationId: location.id });

    return { groupInvite, parentRecurringGroupInvite, changeset, handsontable, location, flows };
  }

  @routeEvent
  routeWillChange(transition: Transition): void {
    const { changeset, parentRecurringGroupInvite } = this.modelFor(
      this.routeName,
    ) as VisitorsGroupInvitesShowRouteModel;
    const isDirty = changeset?.isDirty;

    if (!isDirty) return;

    void this.transitionConfirm.displayConfirmTask.perform(transition, {
      continue: () => {
        changeset?.rollback();
        parentRecurringGroupInvite?.rollbackAttributes();
      },
    });
  }

  maybeSwitchCurrentLocation(currentLocationId?: string, groupInviteLocationId?: string): void {
    if (currentLocationId && groupInviteLocationId && currentLocationId !== groupInviteLocationId) {
      // eslint-disable-next-line ember/no-controller-access-in-routes
      const protectedController = this.controllerFor('protected');

      protectedController.send('switchLocation', groupInviteLocationId);
    }
  }
}
